/**
 * useProfileVideo Hook
 *
 * Fetches the profile's native videos and returns the latest one (single-video
 * UX). While that video is still transcoding (pending/processing) the query
 * polls every 5s so the UI flips to the player once it's `ready`.
 */

import { useQuery } from "@tanstack/react-query"
import { listProfileVideos, type ProfileVideoItem } from "../services/profileVideo.service"

async function getLatestProfileVideo(userId: string): Promise<ProfileVideoItem | null> {
  const videos = await listProfileVideos(userId)
  if (videos.length === 0) return null
  // Latest by createdAt (the single-video UX shows the most recent upload).
  return [...videos].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
}

export function useProfileVideo(userId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["video", userId],
    queryFn: () => getLatestProfileVideo(userId),
    enabled: !!userId && (options?.enabled ?? true),
    // Poll while the latest video is still transcoding so the UI updates
    // when it becomes playable.
    refetchInterval: query => {
      const v = query.state.data
      const stillProcessing = v && (v.status === "pending" || v.status === "processing")
      if (!stillProcessing) return false
      // Ceiling so an abandoned tab / server-stuck row doesn't poll forever
      // (~48 * 5s = 4 min). dataUpdateCount increments on each successful fetch.
      return query.state.dataUpdateCount >= 48 ? false : 5000
    },
  })
}
